import {
  deleteNonVwfsLessor,
  editNonVwfsLessor,
  addNonVwfsLessor,
} from "@/app/lib/actions/non-vwfs-lessor";
import { ItemContent, ItemTitle } from "@/components/ui/item";
import {
  ItemsListCard,
  ItemsListCardSkeleton,
} from "@/components/backoffice/items-list-card";
import {
  useNonVwfsLessorsQuery,
  invalidateNonVwfsLessorsQuery,
} from "@/lib/queries";
import { useQueryClient } from "@tanstack/react-query";
import * as z from "zod";

import {
  addNonVwfsLessorSchema,
  editNonVwfsLessorSchema,
} from "@/lib/schemas/non-vwfs-lessors";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { toast } from "sonner";

type NonVwfsLessor = {
  id: number;
  name: string;
  siret: string;
  isActive: boolean;
};

function NonVwfsLessorItemsListCard() {
  const queryClient = useQueryClient();

  const nonVwfsLessorsQuery = useNonVwfsLessorsQuery({ sort: { name: "asc" } });
  const handleCreate = async (
    nonVwfsLessor: z.infer<typeof addNonVwfsLessorSchema>,
  ) => {
    const response = await addNonVwfsLessor(nonVwfsLessor);
    if (!response.success) {
      toast.error(response.message);
    } else {
      toast.success("Nouveau loueur hors VWFS créé.");
      invalidateNonVwfsLessorsQuery(queryClient);
    }
  };

  const handleUpdate = async (
    nonVwfsLessor: z.infer<typeof editNonVwfsLessorSchema>,
  ) => {
    const response = await editNonVwfsLessor(nonVwfsLessor);
    if (!response.success) {
      toast.error(response.message);
    } else {
      toast.success("Loueur hors VWFS modifié.");
      invalidateNonVwfsLessorsQuery(queryClient);
    }
  };

  const handleDelete = async (id: { id: number }) => {
    const response = await deleteNonVwfsLessor(id);
    if (!response.success) {
      toast.error(response.message);
    } else {
      toast.success("Loueur hors VWFS supprimé.");
      invalidateNonVwfsLessorsQuery(queryClient);
    }
  };

  if (nonVwfsLessorsQuery.isPending) return <ItemsListCardSkeleton />;
  const nonVwfsLessors = nonVwfsLessorsQuery.data?.nonVwfsLessors ?? [];

  return (
    <ItemsListCard<NonVwfsLessor>
      title="Loueurs hors VWFS"
      items={nonVwfsLessors}
      createSchema={addNonVwfsLessorSchema}
      editSchema={editNonVwfsLessorSchema}
      defaultCreateValues={{ name: "", siret: "", isActive: true }}
      onCreate={handleCreate}
      onUpdate={handleUpdate}
      onDelete={handleDelete}
      renderItem={(item) => (
        <>
          <ItemContent>
            <ItemTitle>{item.name}</ItemTitle>
          </ItemContent>
        </>
      )}
      renderFormFields={(form) => (
        <FieldGroup>
          <form.Field name="name">
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;
              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>Libellé</FieldLabel>
                  <Input
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={isInvalid}
                    maxLength={200}
                  />
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              );
            }}
          </form.Field>
          <form.Field name="siret">
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;
              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>SIRET</FieldLabel>
                  <Input
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={isInvalid}
                    maxLength={14}
                  />

                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              );
            }}
          </form.Field>
          <form.Field name="isActive">
            {(field) => (
              <Field>
                <FieldLabel htmlFor={field.name}>Statut</FieldLabel>
                <Switch
                  id="switch"
                  checked={field.state.value}
                  onCheckedChange={field.handleChange}
                />
              </Field>
            )}
          </form.Field>
        </FieldGroup>
      )}
    />
  );
}

export { NonVwfsLessorItemsListCard };
